import {
  createContext,
  useState,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import { UseFormReturnType, useForm } from '@mantine/form';

import { useSearchParams } from 'react-router-dom';
import { emailValidator, passwordValidator } from '../../utils';

import { SignUpRequest } from '../../@types/company';
import { AMBANK_RESELLER_ID } from '../../constants';

interface SignUpFormValues
  extends Omit<
    SignUpRequest,
    | 'captcha'
    | 'locations'
    | 'sale_id'
    | 'reseller_id'
    | 'referral_staff_id'
    | 'hs_deal_id'
  > {
  locations: string[];
  password_confirmation?: string;
  tncAgreed?: boolean;
  dataAgreement?: boolean;
}

interface ISignUpFormContext {
  activeStep: number;
  previousStep: () => void;
  nextStep: () => void;
  form: UseFormReturnType<SignUpFormValues>;
}

const SignUpFormContext = createContext<ISignUpFormContext | undefined>(
  undefined,
);

export function useSignUpFormContext() {
  const signUpForm = useContext(SignUpFormContext);

  if (!signUpForm) {
    throw new Error(
      'useSignUpFormContext must be used within a SignUpFormProvider',
    );
  }

  return signUpForm;
}

/**
 *
 * #6214 - CASA Bank Provisioning
 * Returns an object to render error messages on validation.
 *
 * Bank reseller codes:
 * - 69 represents AmBank
 *
 * scenarios:
 * 1. if reseller_id not 69, return and show standard validation for tncAgreed only.
 * 2. if reseller_id is 69:
 *  - if either tnc or data agreement is not checked, display error as one underneath
 *    data agreement checkbox rather than for both tnc and data agreement.
 *
 * @param params.resellerId
 * @param params.dataAgreement
 * @param params.tncAgreed
 */
function getTNCError(params: {
  resellerId: string;
  tncAgreed: boolean;
  dataAgreement?: boolean;
}): { tncAgreed: string | null; dataAgreement: string | null } {
  if (params.resellerId === AMBANK_RESELLER_ID) {
    if (!params.tncAgreed || !params.dataAgreement) {
      return {
        tncAgreed: ' ',
        dataAgreement: 'You must agree to our Terms of Use and Privacy Policy.',
      };
    }
  }

  return {
    dataAgreement: null,
    tncAgreed: params.tncAgreed
      ? null
      : 'You must agree to our Terms of Use and Privacy Policy.',
  };
}

export const SignUpFormProvider = ({
  children,
}: React.ComponentPropsWithoutRef<'div'>) => {
  const [activeStep, setActiveStep] = useState(0);
  const [searchParams] = useSearchParams();
  const resellerId = searchParams.get('reseller_id') || searchParams.get('rid');

  const form = useForm<SignUpFormValues>({
    initialValues: {
      company_name: '',
      company_size: '0',
      // company_industry: '',
      locations: [],
      pcb_question: '',
      full_name: '',
      contact_number: '',
      email: '',
      password: '',
      password_confirmation: '',
      tncAgreed: false,
      // #6214 ambank
      ambank_account_no: '',
      dataAgreement: false,
    },
    validate: values => {
      if (activeStep === 0) {
        return {
          company_name: !values.company_name
            ? 'Company name is required'
            : null,
          company_size:
            !values.company_size || values.company_size === '0'
              ? 'Company size is required'
              : null,
          // company_industry: !values.company_industry
          //   ? 'Company Industry is required'
          //   : null,
          locations: !values.locations.length
            ? 'At least one location is required'
            : null,
        };
      }

      if (activeStep === 1) {
        return {
          full_name: !values.full_name ? 'Full name is required' : null,
          contact_number: !values.contact_number
            ? 'Phone number is required'
            : null,
          email: emailValidator(values.email),
          password: passwordValidator(values.password),
          password_confirmation:
            values.password !== values.password_confirmation
              ? 'Passwords do not match'
              : null,
          ...getTNCError({
            resellerId: resellerId ?? '',
            tncAgreed: values.tncAgreed ?? false,
            dataAgreement: values.dataAgreement ?? false,
          }),
        };
      }

      return {};
    },
  });

  const nextStep = useCallback(() => {
    setActiveStep(currentStep => {
      if (form.validate().hasErrors) {
        return currentStep;
      }
      return currentStep < 3 ? currentStep + 1 : currentStep;
    });
  }, [form]);

  const previousStep = useCallback(() => {
    setActiveStep(currentStep => (currentStep > 0 ? currentStep - 1 : 0));
  }, []);

  const value = useMemo(
    () => ({ activeStep, previousStep, nextStep, form }),
    [activeStep, form, previousStep, nextStep],
  );

  return (
    <SignUpFormContext.Provider value={value}>
      {children}
    </SignUpFormContext.Provider>
  );
};
